/**
 * Meta (Facebook) conversion tracking.
 *
 * Every event fires twice — once from the browser via the Pixel, once from our
 * server via the Conversions API — sharing one `eventID`. Meta deduplicates on
 * that id, so we get one conversion, not two, while still reporting the event
 * when an ad blocker or iOS tracking prevention kills the browser call. On
 * mobile Meta traffic that browser-side loss is routinely 20-30%, which is the
 * difference between a campaign that optimises and one that doesn't.
 *
 * Nothing here throws: tracking must never be able to break checkout.
 */

import { getAttribution, randomId } from './attribution';
import { postJson } from './api';

const PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID || '';

let pixelReady = false;

/** True when a pixel ID is configured. Tracking is a no-op otherwise. */
export const isTrackingEnabled = () => Boolean(PIXEL_ID);

/**
 * Inject Meta's pixel bootstrap.
 *
 * Loaded at runtime rather than hardcoded in index.html so the site ships with
 * no third-party script at all when no pixel is configured — which keeps local
 * development and preview builds clean.
 */
export function initPixel() {
  if (!PIXEL_ID || pixelReady || typeof window === 'undefined') return;

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', PIXEL_ID);
  pixelReady = true;
}

/**
 * Hash a value with SHA-256 for Meta's advanced matching.
 *
 * Email and phone are never sent in the clear. Meta requires lowercase,
 * trimmed, hex-encoded SHA-256; phone numbers must be digits only with country
 * code and no leading `+`.
 */
async function sha256(value) {
  if (!value || typeof crypto === 'undefined' || !crypto.subtle) return null;
  try {
    const data = new TextEncoder().encode(String(value).trim().toLowerCase());
    const digest = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return null;
  }
}

/** Normalise an Indian phone number to Meta's expected form: 919876543210. */
function normalisePhone(phone) {
  if (!phone) return null;
  let digits = String(phone).replace(/\D/g, '');
  if (digits.length === 10) digits = `91${digits}`;
  if (digits.startsWith('0')) digits = `91${digits.slice(1)}`;
  return digits || null;
}

/** Build the hashed user block Meta uses to match a conversion to a person. */
async function buildUserData(user = {}) {
  const [em, ph, fn, ct] = await Promise.all([
    sha256(user.email),
    sha256(normalisePhone(user.phone)),
    sha256(user.name ? String(user.name).split(' ')[0] : null),
    sha256(user.city),
  ]);

  const attribution = getAttribution();

  return {
    ...(em ? { em } : {}),
    ...(ph ? { ph } : {}),
    ...(fn ? { fn } : {}),
    ...(ct ? { ct } : {}),
    ...(attribution.fbp ? { fbp: attribution.fbp } : {}),
    ...(attribution.fbc ? { fbc: attribution.fbc } : {}),
    country: await sha256('in'),
  };
}

/**
 * Fire a standard Meta event on both channels.
 *
 * @param {string} eventName  A Meta standard event (Lead, Purchase, …).
 * @param {object} custom     Event parameters (value, currency, content_name…).
 * @param {object} user       Raw user details — hashed before they leave here.
 */
export async function track(eventName, custom = {}, user = {}) {
  if (!PIXEL_ID) return null;

  const eventId = randomId('ev');
  const eventSourceUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Browser side — fires immediately so Events Manager shows it live.
  try {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', eventName, custom, { eventID: eventId });
    }
  } catch {
    // Pixel blocked. The server call below is exactly the fallback for this.
  }

  // Server side — survives ad blockers and ITP.
  try {
    const userData = await buildUserData(user);
    await postJson('/api/meta/track', {
      eventName,
      eventId,
      eventSourceUrl,
      customData: custom,
      userData,
    });
  } catch {
    // Never let a reporting failure surface to the visitor.
  }

  return eventId;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Named events for the funnel.

   Mapping each step to the Meta standard event that campaign optimisation
   actually understands:

     PageView        landing
     ViewContent     opened a workshop's details
     Lead            submitted contact details  ← optimise the campaign on this
     InitiateCheckout sent to the payment gateway
     Purchase        payment confirmed          ← the ROAS number
   ───────────────────────────────────────────────────────────────────────── */

export const trackPageView = () => {
  if (!PIXEL_ID) return;
  try {
    window.fbq?.('track', 'PageView');
  } catch {
    /* no-op */
  }
};

export const trackViewContent = (workshop) =>
  track('ViewContent', {
    content_type: 'product',
    content_ids: [workshop.id],
    content_name: workshop.title,
    content_category: workshop.category,
    value: workshop.price,
    currency: 'INR',
  });

export const trackLead = (workshop, student) =>
  track(
    'Lead',
    {
      content_name: workshop?.title || 'Workshop enquiry',
      content_category: workshop?.category || 'General',
      value: workshop?.price || 0,
      currency: 'INR',
    },
    student
  );

export const trackInitiateCheckout = (registration) =>
  track(
    'InitiateCheckout',
    {
      content_type: 'product',
      content_ids: [registration.workshopId],
      content_name: registration.workshopTitle,
      value: registration.totalPaid,
      currency: 'INR',
      num_items: 1,
    },
    { email: registration.email, phone: registration.phone, name: registration.studentName, city: registration.city }
  );

export const trackPurchase = (registration) =>
  track(
    'Purchase',
    {
      content_type: 'product',
      content_ids: [registration.workshopId],
      content_name: registration.workshopTitle,
      value: registration.totalPaid,
      currency: 'INR',
      num_items: 1,
      order_id: registration.id,
    },
    { email: registration.email, phone: registration.phone, name: registration.studentName, city: registration.city }
  );

export const trackCompleteRegistration = (workshop, student) =>
  track(
    'CompleteRegistration',
    {
      content_name: workshop?.title,
      value: workshop?.price || 0,
      currency: 'INR',
      status: true,
    },
    student
  );
